import React, { ReactNode, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";

import { ReactComponent as ChevronSliderIcon } from "assets/images/landing/chevron-slider-icon.svg";

import { styles } from "./styles";

interface CarouselProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  visibleCount?: number;
  step?: number;
  gap?: number;
  getItemKey?: (item: T, index: number) => string | number;
  onNavigate?: (direction: "left" | "right") => void;
  leftArrow?: ReactNode;
  rightArrow?: ReactNode;
}

const FADE_WIDTH = 24;

function Carousel<T>(props: CarouselProps<T>) {
  const {
    items,
    renderItem,
    visibleCount = 2,
    step = 1,
    gap = 20,
    getItemKey,
    onNavigate,
    leftArrow,
    rightArrow,
  } = props;

  const [activeIndex, setActiveIndex] = useState(0);

  const maxIndex = Math.max(0, items.length - visibleCount);
  const canGoLeft = activeIndex > 0;
  const canGoRight = activeIndex < maxIndex;

  const itemBasis = useMemo(
    () => `calc((100% - ${gap * (visibleCount - 1)}px) / ${visibleCount})`,
    [gap, visibleCount],
  );

  const trackTransform = useMemo(() => {
    const offsetPercent = (activeIndex * 100) / visibleCount;
    const offsetGap = (activeIndex * gap) / visibleCount;
    return `translateX(calc(-${offsetPercent}% - ${offsetGap}px))`;
  }, [activeIndex, gap, visibleCount]);

  const fadeMask = canGoRight
    ? `linear-gradient(to right, black calc(100% - ${FADE_WIDTH}px), transparent 100%)`
    : undefined;

  const goLeft = () => {
    setActiveIndex((prev) => Math.max(0, prev - step));
    onNavigate?.("left");
  };
  const goRight = () => {
    setActiveIndex((prev) => Math.min(maxIndex, prev + step));
    onNavigate?.("right");
  };

  return (
    <Stack sx={styles.root}>
      <IconButton
        onClick={goLeft}
        disabled={!canGoLeft}
        sx={!canGoLeft ? styles.arrowHidden : undefined}
        aria-label="previous"
      >
        {leftArrow ?? <ChevronSliderIcon />}
      </IconButton>

      <Box
        sx={{
          ...styles.viewport,
          maskImage: fadeMask,
          WebkitMaskImage: fadeMask,
        }}
      >
        <Box sx={{ ...styles.track, gap: `${gap}px`, transform: trackTransform }}>
          {items.map((item, index) => (
            <Box
              key={getItemKey ? getItemKey(item, index) : index}
              sx={{ flex: `0 0 ${itemBasis}` }}
            >
              {renderItem(item, index)}
            </Box>
          ))}
        </Box>
      </Box>

      <IconButton
        onClick={goRight}
        disabled={!canGoRight}
        sx={!canGoRight ? styles.arrowHidden : undefined}
        aria-label="next"
      >
        {rightArrow ?? (
          <ChevronSliderIcon style={{ transform: "rotate(180deg)" }} />
        )}
      </IconButton>
    </Stack>
  );
}

export default Carousel;
